import {
  GameState,
  GameSettings,
  RuntimeState,
  GamePhase,
  GameEvent,
  Manifest,
} from '../types';
import { EventBus } from './GameEvents';
import { AudioEngine } from '../services/AudioEngine';
import { AudioAssetResolver } from '../services/AudioAssetResolver';
import { AudioPreloader } from '../services/AudioPreloader';
import { StatePersistence } from '../services/StatePersistence';
import { TimerManager } from '../services/TimerManager';
import { NetworkDetector } from '../services/NetworkDetector';
import { fisherYatesShuffle, generateRange } from '../utils/shuffle';
import { CONSECUTIVE_FAILURE_LIMIT } from '../constants/defaults';
import { formatElapsedTime, getElapsedMs } from '../utils/time';

export class GameStateMachine {
  private state: RuntimeState;
  private events: EventBus = new EventBus();
  private audioEngine: AudioEngine;
  private audioResolver: AudioAssetResolver;
  private preloader: AudioPreloader;
  private persistence: StatePersistence;
  private timer: TimerManager;
  private networkDetector: NetworkDetector;
  private manifest: Manifest | null = null;

  constructor(
    audioEngine: AudioEngine,
    audioResolver: AudioAssetResolver,
    preloader: AudioPreloader,
    persistence: StatePersistence,
    timer: TimerManager,
    networkDetector: NetworkDetector
  ) {
    this.audioEngine = audioEngine;
    this.audioResolver = audioResolver;
    this.preloader = preloader;
    this.persistence = persistence;
    this.timer = timer;
    this.networkDetector = networkDetector;

    this.state = this.createDefaultRuntimeState();

    // Wire up audio engine callbacks
    this.audioEngine.onComplete = (number, skipped) => this.handleAudioComplete(number, skipped);
    this.audioEngine.onError = (number, error) => this.handleAudioError(number, error);
    this.audioEngine.onLongAudio = (number, elapsed) => {
      this.events.emit('audioStarted', number, elapsed);
    };

    // Wire up timer
    this.timer.onExpired = () => this.handleTimerExpired();
    this.timer.onTick = (remaining) => this.events.emit('timerTick', remaining);
  }

  initialize(manifest: Manifest): void {
    this.manifest = manifest;

    // Try to load saved state
    const saved = this.persistence.load();
    if (saved && this.validateSavedState(saved)) {
      this.state = {
        ...saved,
        isAudioPlaying: false,
        isAutoPlayPaused: false,
        autoPlayPausedByModal: false,
        consecutiveAudioFailures: 0,
        emergencyMode: false,
        silentMode: false,
        audioFirstMode: true,
      };
    } else {
      const defaultState = this.persistence.getDefaultState(manifest);
      this.state = {
        ...defaultState,
        isAudioPlaying: false,
        isAutoPlayPaused: false,
        autoPlayPausedByModal: false,
        consecutiveAudioFailures: 0,
        emergencyMode: false,
        silentMode: false,
        audioFirstMode: true,
      };
    }

    this.events.emit('stateChanged', this.state);
    this.events.emit('phaseChanged', this.state.gamePhase);
  }

  startGame(): void {
    console.log('[GameStateMachine] startGame called, current phase:', this.state.gamePhase);
    if (this.state.gamePhase !== 'IDLE') {
      console.log('[GameStateMachine] startGame rejected - not in IDLE state');
      return;
    }

    const { min, max } = this.state.settings;
    console.log('[GameStateMachine] Starting game with range:', min, 'to', max);
    const range = generateRange(min, max);
    console.log('[GameStateMachine] Generated range:', range, 'length:', range.length);
    const shuffled = fisherYatesShuffle(range);
    console.log('[GameStateMachine] Shuffled queue:', shuffled, 'length:', shuffled.length);

    this.state.session.calledNumbers = [];
    this.state.session.currentNumber = null;
    this.state.session.shuffledQueue = shuffled;
    this.state.session.startTime = new Date().toISOString();
    this.state.gamePhase = 'PLAYING';

    console.log('[GameStateMachine] Game started, queue length:', this.state.session.shuffledQueue.length);

    // Initialize audio engine on user gesture
    this.audioEngine.initialize();

    this.persistence.save(this.getPersistedState());
    this.preloadUpcoming();

    this.events.emit('stateChanged', this.state);
    this.events.emit('phaseChanged', 'PLAYING');
  }

  nextNumber(): void {
    console.log('[GameStateMachine] nextNumber called, phase:', this.state.gamePhase, 'queue length:', this.state.session.shuffledQueue.length, 'audio playing:', this.state.isAudioPlaying);
    if (this.state.gamePhase !== 'PLAYING') {
      console.log('[GameStateMachine] nextNumber rejected - not in PLAYING state');
      return;
    }
    if (this.state.isAudioPlaying) {
      console.log('[GameStateMachine] nextNumber rejected - audio already playing');
      return;
    }

    if (this.state.session.shuffledQueue.length === 0) {
      console.log('[GameStateMachine] Queue empty, completing game');
      this.completeGame();
      return;
    }

    // Pop next number
    const number = this.state.session.shuffledQueue.shift()!;
    this.state.session.calledNumbers.push(number);
    this.state.session.currentNumber = number;
    this.state.isAudioPlaying = true;

    // Set start time if first number
    if (!this.state.session.startTime) {
      this.state.session.startTime = new Date().toISOString();
    }

    // Stop timer during audio playback
    if (this.timer.isRunning()) {
      this.timer.stop();
    }

    this.events.emit('audioStarted', number);
    this.events.emit('stateChanged', this.state);

    // Play audio
    if (this.state.silentMode) {
      // Silent mode: skip audio entirely
      this.handleAudioComplete(number, false);
    } else {
      const url = this.audioResolver.resolve(
        this.state.settings.language,
        this.state.settings.style,
        number
      );
      this.audioEngine.play(url, number).catch(() => {
        // Error handled by onError callback
      });
    }
  }

  forceSkipAudio(): void {
    if (this.state.isAudioPlaying) {
      this.audioEngine.forceSkip();
    }
  }

  reset(): void {
    // Stop everything
    if (this.state.isAudioPlaying) {
      this.audioEngine.forceSkip();
    }
    this.timer.stop();
    this.preloader.invalidateCache();

    this.state.session.calledNumbers = [];
    this.state.session.currentNumber = null;
    this.state.session.shuffledQueue = [];
    this.state.session.startTime = null;
    this.state.gamePhase = 'IDLE';
    this.state.isAudioPlaying = false;
    this.state.isAutoPlayPaused = false;
    this.state.autoPlayPausedByModal = false;
    this.state.consecutiveAudioFailures = 0;
    this.state.silentMode = false;

    this.persistence.save(this.getPersistedState());
    this.events.emit('stateChanged', this.state);
    this.events.emit('phaseChanged', 'IDLE');
  }

  applySettings(settings: GameSettings): void {
    const oldLang = this.state.settings.language;
    const oldStyle = this.state.settings.style;

    this.state.settings = { ...settings };

    // If language or style changed, invalidate preloader cache
    if (settings.language !== oldLang || settings.style !== oldStyle) {
      this.preloader.invalidateCache();
      if (this.state.gamePhase === 'PLAYING') {
        this.preloadUpcoming();
      }
    }

    this.persistence.save(this.getPersistedState());
    this.events.emit('settingsChanged', settings);
    this.events.emit('stateChanged', this.state);
  }

  toggleAutoPlay(): void {
    if (this.state.gamePhase === 'COMPLETE') return;

    this.state.settings.autoPlayEnabled = !this.state.settings.autoPlayEnabled;

    if (this.state.settings.autoPlayEnabled) {
      // Start timer if in PLAYING and not currently playing audio
      if (this.state.gamePhase === 'PLAYING' && !this.state.isAudioPlaying) {
        this.startAutoPlayTimer();
      }
    } else {
      this.timer.stop();
    }

    this.persistence.save(this.getPersistedState());
    this.events.emit('autoPlayToggled', this.state.settings.autoPlayEnabled);
    this.events.emit('stateChanged', this.state);
  }

  pauseAutoPlay(byModal: boolean = false): void {
    if (!this.state.settings.autoPlayEnabled) return;
    if (this.timer.isRunning()) {
      this.timer.pause();
      this.state.isAutoPlayPaused = true;
      if (byModal) this.state.autoPlayPausedByModal = true;
      this.events.emit('autoPlayPaused');
    }
  }

  resumeAutoPlay(fromModal: boolean = false): void {
    if (!this.state.settings.autoPlayEnabled) return;
    if (fromModal && !this.state.autoPlayPausedByModal) return;

    if (this.state.isAutoPlayPaused) {
      this.timer.resume();
      this.state.isAutoPlayPaused = false;
      this.state.autoPlayPausedByModal = false;
      this.events.emit('autoPlayResumed');
    }
  }

  enterSilentMode(): void {
    this.state.silentMode = true;
    this.state.settings.autoPlayEnabled = false;
    this.timer.stop();
    this.events.emit('stateChanged', this.state);
  }

  setEmergencyMode(emergency: boolean): void {
    this.state.emergencyMode = emergency;
    if (emergency) {
      this.events.emit('emergencyMode');
    }
    this.events.emit('stateChanged', this.state);
  }

  getState(): RuntimeState {
    return { ...this.state };
  }

  getCalledCount(): number {
    return this.state.session.calledNumbers.length;
  }

  getRemainingCount(): number {
    return this.state.session.shuffledQueue.length;
  }

  getTotalCount(): number {
    const { min, max } = this.state.settings;
    return max - min + 1;
  }

  getElapsedTime(): string {
    const ms = getElapsedMs(this.state.session.startTime);
    return formatElapsedTime(ms);
  }

  on(event: GameEvent, handler: (...args: unknown[]) => void): void {
    this.events.on(event, handler);
  }

  off(event: GameEvent, handler: (...args: unknown[]) => void): void {
    this.events.off(event, handler);
  }

  // --- Private Methods ---

  private handleAudioComplete(number: number, skipped: boolean): void {
    this.state.isAudioPlaying = false;
    this.state.consecutiveAudioFailures = 0;

    this.persistence.save(this.getPersistedState());
    this.preloadUpcoming();

    this.events.emit('numberCalled', number, skipped);
    this.events.emit('stateChanged', this.state);

    // Check if game complete
    if (this.state.session.shuffledQueue.length === 0) {
      this.completeGame();
      return;
    }

    // Start auto play timer after audio completes
    if (this.state.settings.autoPlayEnabled && this.state.gamePhase === 'PLAYING') {
      this.startAutoPlayTimer();
    }
  }

  private handleAudioError(number: number, error: Error): void {
    console.error(`[GameStateMachine] Audio error for number ${number}:`, error);

    this.state.isAudioPlaying = false;
    this.state.consecutiveAudioFailures++;

    // Save state even on error (graceful degradation)
    this.persistence.save(this.getPersistedState());

    this.events.emit('audioError', { number, error });
    this.events.emit('numberCalled', number, false); // Still show the number
    this.events.emit('stateChanged', this.state);

    // Check consecutive failure limit
    if (this.state.consecutiveAudioFailures >= CONSECUTIVE_FAILURE_LIMIT) {
      this.state.settings.autoPlayEnabled = false;
      this.timer.stop();
      this.events.emit('autoPlayToggled', false);
      return;
    }

    // Check if game complete
    if (this.state.session.shuffledQueue.length === 0) {
      this.completeGame();
      return;
    }

    // Auto play continues on error (start immediately since no audio to wait for)
    if (this.state.settings.autoPlayEnabled && this.state.gamePhase === 'PLAYING') {
      this.startAutoPlayTimer();
    }
  }

  private handleTimerExpired(): void {
    if (this.state.gamePhase === 'PLAYING' && !this.state.isAudioPlaying) {
      this.nextNumber();
    }
  }

  private completeGame(): void {
    console.log('[GameStateMachine] completeGame called');
    this.state.gamePhase = 'COMPLETE';
    this.state.settings.autoPlayEnabled = false;
    this.timer.stop();

    this.persistence.save(this.getPersistedState());
    this.events.emit('gameComplete');
    this.events.emit('phaseChanged', 'COMPLETE');
    this.events.emit('stateChanged', this.state);
  }

  private startAutoPlayTimer(): void {
    this.timer.start(this.state.settings.autoPlayInterval);
  }

  private preloadUpcoming(): void {
    if (this.state.session.shuffledQueue.length === 0) return;
    this.preloader.configure(this.networkDetector.getSpeed());
    this.preloader.preloadNext(
      this.state.session.shuffledQueue,
      this.audioResolver,
      this.state.settings.language,
      this.state.settings.style
    );
  }

  private validateSavedState(saved: GameState): boolean {
    if (!this.manifest) return false;

    const { language, style } = saved.settings;
    if (!this.manifest.languages[language]) return false;
    if (!this.manifest.languages[language].styles[style]) return false;

    return true;
  }

  private getPersistedState(): GameState {
    return {
      version: this.state.version,
      timestamp: this.state.timestamp,
      gamePhase: this.state.gamePhase,
      settings: { ...this.state.settings },
      session: {
        calledNumbers: [...this.state.session.calledNumbers],
        currentNumber: this.state.session.currentNumber,
        shuffledQueue: [...this.state.session.shuffledQueue],
        startTime: this.state.session.startTime,
      },
    };
  }

  private createDefaultRuntimeState(): RuntimeState {
    return {
      version: 4,
      timestamp: new Date().toISOString(),
      gamePhase: 'IDLE',
      settings: {
        language: 'vi',
        style: 'tet_traditional',
        min: 1,
        max: 99,
        autoPlayEnabled: false,
        autoPlayInterval: 5,
        uiLocale: 'vi',
      },
      session: {
        calledNumbers: [],
        currentNumber: null,
        shuffledQueue: [],
        startTime: null,
      },
      isAudioPlaying: false,
      isAutoPlayPaused: false,
      autoPlayPausedByModal: false,
      consecutiveAudioFailures: 0,
      emergencyMode: false,
      silentMode: false,
      audioFirstMode: true,
    };
  }
}
